import React, { useState, useEffect } from 'react';
import Switch from '../../components/ui/Switch';
import { getCustomerNotificationSettings, updateCustomerNotificationSettings } from '../../services/settings';
import toast from 'react-hot-toast';

const CATEGORY_LABELS = {
  account: 'Account & signup',
  license: 'Licence',
  quotation: 'Quotation',
  translation: 'Translation',
  payment: 'Payment & invoice',
  message: 'Messages',
  promotional: 'Promotional & platform updates'
};

const NotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [initialPrefs, setInitialPrefs] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { customerNotificationPrefs = {}, customerNotificationTypes = [] } = await getCustomerNotificationSettings();
        if (cancelled) return;
        setTypes(customerNotificationTypes);
        setPrefs(customerNotificationPrefs);
        setInitialPrefs(JSON.parse(JSON.stringify(customerNotificationPrefs)));
      } catch (e) {
        if (!cancelled) toast.error(e?.message || 'Failed to load notification settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getPref = (key, channel) => {
    const p = prefs[key];
    if (p && typeof p[channel] === 'boolean') return p[channel];
    return true;
  };

  const updatePref = (key, channel, value) => {
    setPrefs(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || { email: true, inApp: true }),
        [channel]: value
      }
    }));
  };

  const handleRevert = () => {
    setPrefs(JSON.parse(JSON.stringify(initialPrefs)));
    toast.success('Reverted to last saved preferences');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCustomerNotificationSettings(prefs);
      setInitialPrefs(JSON.parse(JSON.stringify(prefs)));
      toast.success('Customer notification settings saved');
    } catch (e) {
      toast.error(e?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const byCategory = types.reduce((acc, t) => {
    const cat = t.category || 'system';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const categoryOrder = ['account', 'license', 'quotation', 'translation', 'payment', 'message', 'promotional'];
  const orderedCategories = categoryOrder.filter(c => byCategory[c]?.length);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-100 rounded w-2/3" />
          <div className="h-32 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="mb-8">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Customer notification preferences</h3>
          <p className="text-sm text-gray-500 mb-6">
            Control which notifications and emails are sent to customers. Toggle off any type to stop sending to all customers. Changes apply globally.
          </p>

          {orderedCategories.map(cat => (
            <div key={cat} className="mb-8">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">{CATEGORY_LABELS[cat] || cat}</h4>
              <div className="space-y-4">
                {byCategory[cat].map(({ key, label, description }) => (
                  <div key={key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-gray-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{label}</p>
                      {description && (
                        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-8 flex-shrink-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs text-gray-500 whitespace-nowrap min-w-[3.5rem]">Email</span>
                        <Switch
                          checked={getPref(key, 'email')}
                          onChange={() => updatePref(key, 'email', !getPref(key, 'email'))}
                          label={`${label} email`}
                        />
                      </div>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs text-gray-500 whitespace-nowrap min-w-[3.5rem]">In-app</span>
                        <Switch
                          checked={getPref(key, 'inApp')}
                          onChange={() => updatePref(key, 'inApp', !getPref(key, 'inApp'))}
                          label={`${label} in-app`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleRevert}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Revert preferences
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-400 rounded-lg hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
